import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InviteUserForm from '../components/admin/InviteUserForm';
import InvitationList from '../components/admin/InvitationList';
import OrganisationSettings from '../components/admin/OrganisationSettings';
import SubscriptionManagers from '../components/admin/SubscriptionManagers';
import UserStatusManagement from '../components/admin/UserStatusManagement';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';
import { isOrgAdmin } from '../utils/auth';
import { Users, ChevronRight, ChevronDown, Shield } from 'lucide-react';


function AdminManagement() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [isManagersExpanded, setIsManagersExpanded] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const isAdmin = isOrgAdmin(user);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
          <p className="text-sm text-neutral-500 mt-1">Organisation Admin is restricted to admin users.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 text-sm text-primary-600 hover:underline"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const handleInviteSent = () => {
    // Refresh the invitation list when a new invitation is sent
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Organisation Admin"
        subtitle="Manage user invitations, organisation admins and organisation settings"
        icon={<Users className="h-6 w-6 text-white" />}
      />

      <div key={user?.organisation_id} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Invite User Form */}
          <div>
            <InviteUserForm onInviteSent={handleInviteSent} />
          </div>

          {/* Recent Invitations */}
          <div>
            <InvitationList key={refreshKey} />
          </div>
        </div>

        {/* User Account Status */}
        <div className="mt-8">
          <UserStatusManagement />
        </div>

        {/* Subscription Managers */}
        <div className="mt-8">
          <button
            onClick={() => setIsManagersExpanded(prev => !prev)}
            className="w-full flex items-center justify-between bg-white border border-neutral-200 rounded-xl p-6 hover:border-purple-300 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Organisation Admins (Subscription Managers)</h3>
                <p className="text-sm text-neutral-500">Designate which users are admins — can invite users, purchase modules, and assign licence seats</p>
              </div>
            </div>
            {isManagersExpanded
              ? <ChevronDown className="h-5 w-5 text-neutral-400" />
              : <ChevronRight className="h-5 w-5 text-neutral-400" />
            }
          </button>
          {isManagersExpanded && (
            <div className="mt-4">
              <SubscriptionManagers />
            </div>
          )}
        </div>

        {/* Organisation Settings */}
        <div className="mt-8">
          <OrganisationSettings />
        </div>

        {/* Usage Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start">
            <svg className="h-6 w-6 text-blue-400 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-2">How User Invitations Work</h3>
              <div className="text-blue-800 space-y-2">
                <p>1. <strong>Send Invitation:</strong> Enter the user's email address and choose expiration time</p>
                <p>2. <strong>Email Delivered:</strong> User receives an invitation email with a secure registration link</p>
                <p>3. <strong>User Registers:</strong> They click the link, complete registration, and are automatically added to FactreeTech</p>
                <p>4. <strong>Access Granted:</strong> User can immediately access the system with their new account</p>
                <p>
                  <strong>Already has an account?</strong> Invite them the same way. Their email asks them to sign in
                  and accept, and they join with the account they have, keeping any other organisation they belong
                  to. They appear in your users once they accept.
                </p>
              </div>
              <div className="mt-4 p-3 bg-blue-100 rounded-md">
                <p className="text-sm text-blue-700">
                  <strong>💡 Tip:</strong> Invitation links are single-use and expire automatically for security.
                  Users must register with the exact email address the invitation was sent to.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminManagement;
